"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import ScoreDisplay from "@/components/ScoreDisplay";
import SpinningWheel from "@/components/SpinningWheel";
import ResultsSummary from "@/components/ResultsSummary";
import ChaosControl from "@/components/ChaosControl";
import ChaosWheelOverlay from "@/components/ChaosWheelOverlay";
import CrownOverlay from "@/components/CrownOverlay";
import MusicPlayer from "@/components/MusicPlayer";
import IntroScreen from "@/components/IntroScreen";
import TokenBalance from "@/components/TokenBalance";
import ModeSelect from "@/components/ModeSelect";
import MatchLobby from "@/components/MatchLobby";
import OpponentPanel from "@/components/OpponentPanel";
import MatchEnded from "@/components/MatchEnded";
import SpectatorWheel from "@/components/SpectatorWheel";
import LeaderboardPanel from "@/components/LeaderboardPanel";
import TokensPanel from "@/components/TokensPanel";
import ChaosEffectsPanel from "@/components/ChaosEffectsPanel";
import DemoRun from "@/components/DemoRun";
import { clearActiveRun, DEMO_KEY } from "@/lib/runPersistence";
import { submitScore } from "@/lib/leaderboard";
import { useGameState } from "@/hooks/useGameState";
import { useFireSound } from "@/hooks/useFireSound";
import { useAuth } from "@/hooks/useAuth";
import { useMatch } from "@/hooks/useMatch";
import { playChaosSound, playTick, unlockAudio } from "@/lib/sounds";
import {
  getIntroSeenLocal,
  setIntroSeenLocal,
  setIntroSeenRemote,
} from "@/lib/introSeen";
import {
  buildShareLink,
  getGuestIdentity,
  newMatchId,
  type GuestIdentity,
  type MatchRole,
} from "@/lib/multiplayer";

// Lives at module scope, so it survives in-app navigation (the module stays
// loaded) but resets on a genuine page load (refresh / reopen / first visit).
// Once the player has entered the game this load, returning from another in-app
// screen (e.g. their profile) drops them straight back on the wheel instead of
// the welcome screen — which only shows on a real fresh load.
let enteredGameThisLoad = false;

export default function Home() {
  // Two independent game states: the player's real (solo) run, and a separate
  // slot for "vs friend" matches. Keeping them apart means starting a match
  // never touches an in-progress solo run — it's simply set aside and resumes
  // untouched when the match ends.
  const soloGame = useGameState();
  const matchGame = useGameState({ match: true });

  const { user, profile, loading: authLoading } = useAuth();

  // === Multiplayer (Phase 1: async "vs") ===
  const [matchId, setMatchId] = useState<string | null>(null);
  const [matchRole, setMatchRole] = useState<MatchRole>("guest");
  // Seeded from the module flag so an in-app return skips the welcome screen.
  const [modeChosen, setModeChosen] = useState(enteredGameThisLoad);
  const [matchNullified, setMatchNullified] = useState(false);
  const [guestIdentity, setGuestIdentity] = useState<GuestIdentity | null>(null);
  // In-game overlays (leaderboard / tokens) — open in place over the wheel
  // instead of navigating to a separate page. Mutually exclusive.
  const [panel, setPanel] = useState<"leaderboard" | "tokens" | null>(null);
  // Demo mode runs entirely separately from the real run (see DemoRun). It's
  // always reachable, even mid-run, and never touches the real run slot.
  const [demoActive, setDemoActive] = useState(false);

  const inMatch = matchId !== null;
  // The active game while playing: the match slot during a match, otherwise the
  // solo run. Everything in the wheel UI below reads from `game`.
  const game = inMatch ? matchGame : soloGame;
  const {
    hydrated,
    phase,
    currentWheel,
    showValue,
    wheelNumber,
    totalWheels,
    isLastStep,
    results,
    pendingOption,
    spinning,
    showResult,
    spinToken,
    spin,
    handleSpinEnd,
    next,
    reset,
    totalScore,
    rank,
    alignmentLabel,
    leaderboardBoard,
    isSidekick,
    chaosResults,
    chaosEffects,
    chaosCooldownLabel,
    canTriggerChaos,
    chaosOutcome,
    chaosOverlayStage,
    chaosPending,
    chaosTypeSpinToken,
    chaosResultSpinToken,
    triggerChaosManually,
    handleChaosTypeSpinEnd,
    handleChaosResultSpinEnd,
    dismissChaosOverlay,
    fightStep,
    fightResults,
    startFight,
    hasCrown,
    crownStage,
    crownSpinToken,
    crownPendingOption,
    crownBonusOption,
    takeCrown,
    spinCrownWheel,
    handleCrownSpinEnd,
    continueAfterCrown,
    showRestoredToast,
    dismissRestoredToast,
    markSubmitted,
    submittedScoreId,
    cooldownStartedAt,
    rerollWheel,
    wheelRerollsUsed,
    heatLevel,
  } = game;

  // Wheel-fire audio: roar + ignite whoosh that track the positive-streak tier.
  useFireSound(heatLevel);

  const selfId = user?.id ?? guestIdentity?.id ?? "anon";
  const selfName = profile?.username ?? guestIdentity?.name ?? "Player";

  // The welcome screen reflects the SOLO run only (the menu is never shown
  // during a match). A completed run unlocks Play vs Friend and reads "View
  // Stats"; a mid-wheel run reads "Continue Run".
  const runStatus: "none" | "active" | "completed" = !soloGame.hasActiveRun
    ? "none"
    : soloGame.phase === "complete"
      ? "completed"
      : "active";

  const matchInitRef = useRef(false);
  // Match-lifecycle refs (read inside timers/effects without re-triggering them).
  const opponentEverPresentRef = useRef(false);
  const opponentFinishedRef = useRef(false);
  const opponentNameRef = useRef<string | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phaseRef = useRef(phase);
  // Guards the once-per-match auto-save of the match score to the profile.
  const matchSavedRef = useRef(false);

  // Read any match link + establish a stable guest identity, once after mount
  // (window access must be post-mount to match the server-rendered HTML).
  useEffect(() => {
    setGuestIdentity(getGuestIdentity());
    const params = new URLSearchParams(window.location.search);
    const m = params.get("match");
    if (m) {
      setMatchId(m);
      setMatchRole(params.get("r") === "h" ? "host" : "guest");
      setModeChosen(true);
    } else if (params.get("devresume")) {
      // Dev panel jumped us straight into a saved run (see /dev) — skip the
      // mode-select screen and land on whatever the restored run shows.
      setModeChosen(true);
    }
  }, []);

  // Remember (for this page load) that the player has entered the game, so a
  // round-trip to another in-app screen resumes straight onto the wheel.
  useEffect(() => {
    if (modeChosen) enteredGameThisLoad = true;
  }, [modeChosen]);

  const { opponent, opponentSpin, opponentPresent, publish, publishSpin } =
    useMatch(matchId, matchRole, selfId, selfName);
  const spinSeqRef = useRef(0);

  // Entering a match starts a fresh run on the MATCH slot. The host already
  // reset when creating the match (matchInitRef set), so only a guest joining
  // via link reaches the reset here. The solo run lives in its own slot and is
  // untouched, so there's nothing to discard or confirm.
  useEffect(() => {
    if (!inMatch || !matchGame.hydrated || matchInitRef.current) return;
    matchInitRef.current = true;
    matchGame.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inMatch, matchGame.hydrated]);

  // Keep a non-reactive mirror of the phase + the opponent's last-known name and
  // finished status, so the leave-watcher timer can read them at fire time
  // without restarting whenever they change.
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    if (opponent?.name) opponentNameRef.current = opponent.name;
    if (opponent?.finished) opponentFinishedRef.current = true;
  }, [opponent]);

  // Nullify the run if the opponent leaves mid-match. A short grace period
  // absorbs brief disconnects (network blips, a quick refresh). A match that
  // already concluded (both players finished) is left alone — the verdict
  // stands even if someone then closes their tab.
  useEffect(() => {
    if (!inMatch) {
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      return;
    }
    if (opponentPresent) {
      opponentEverPresentRef.current = true;
      if (leaveTimerRef.current) {
        clearTimeout(leaveTimerRef.current);
        leaveTimerRef.current = null;
      }
      return;
    }
    // Opponent not present. Only act if they had actually joined, and don't
    // stack multiple timers.
    if (!opponentEverPresentRef.current || leaveTimerRef.current) return;
    leaveTimerRef.current = setTimeout(() => {
      leaveTimerRef.current = null;
      const concluded =
        phaseRef.current === "complete" && opponentFinishedRef.current;
      if (!concluded) setMatchNullified(true);
    }, 8000);
  }, [inMatch, opponentPresent]);

  // Always clear a pending leave-timer on unmount.
  useEffect(
    () => () => {
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    },
    [],
  );

  const resetMatchRefs = () => {
    matchInitRef.current = false;
    matchSavedRef.current = false;
    opponentEverPresentRef.current = false;
    opponentFinishedRef.current = false;
    opponentNameRef.current = null;
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  // When a match finishes, auto-save the result to the signed-in player's
  // profile history. This never touches the public leaderboard — that's the
  // optional paid (50-token) submission on the results screen. Fires once.
  useEffect(() => {
    if (
      !inMatch ||
      matchGame.phase !== "complete" ||
      !user ||
      matchSavedRef.current ||
      !matchGame.leaderboardBoard
    )
      return;
    matchSavedRef.current = true;
    submitScore(
      {
        name: profile?.username || selfName,
        score: matchGame.totalScore,
        rank: matchGame.rank,
        board: matchGame.leaderboardBoard,
        alignmentLabel: matchGame.alignmentLabel ?? "",
        isSidekick: matchGame.isSidekick,
        hasCrown: matchGame.hasCrown,
        rolledResults: [
          ...matchGame.results,
          ...matchGame.chaosResults,
          ...matchGame.fightResults,
        ],
        userId: user.id,
      },
      false,
      { multiplayer: true },
    ).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inMatch, matchGame.phase, user]);

  // Always available — even mid solo-run. The match runs on its own slot, so
  // the solo run is set aside (never discarded) and resumes when the match ends.
  const handlePlayFriend = () => {
    const id = newMatchId();
    resetMatchRefs();
    matchInitRef.current = true;
    setMatchNullified(false);
    matchGame.reset();
    setMatchRole("host");
    setMatchId(id);
    setModeChosen(true);
    window.history.replaceState(null, "", `/?match=${id}&r=h`);
  };

  // Leave the match (cancel lobby / opponent abandoned / leave after finishing)
  // → clear the match slot and drop back to the welcome screen, where the
  // untouched solo run is waiting.
  const exitMatchToMenu = () => {
    resetMatchRefs();
    matchGame.reset();
    setMatchId(null);
    setMatchRole("guest");
    setMatchNullified(false);
    setModeChosen(false);
    window.history.replaceState(null, "", "/");
  };

  // Decide once whether to show the one-time intro screen. Waits for the
  // game state to hydrate (so we know if a run is in progress) and for auth
  // to settle (so a returning signed-in user's intro_seen flag is honored).
  const [introDecision, setIntroDecision] = useState<
    "pending" | "show" | "hide"
  >("pending");

  useEffect(() => {
    if (introDecision !== "pending" || !hydrated || authLoading) return;
    if (getIntroSeenLocal()) {
      setIntroDecision("hide");
      return;
    }
    if (user && profile?.intro_seen) {
      setIntroSeenLocal();
      setIntroDecision("hide");
      return;
    }
    setIntroDecision("show");
  }, [introDecision, hydrated, authLoading, user, profile]);

  const handleIntroContinue = () => {
    setIntroSeenLocal();
    if (user) setIntroSeenRemote(user.id).catch(() => {});
    setIntroDecision("hide");
  };

  // Enter a fresh demo run. Available from any entry screen, even mid-run — it
  // never touches the real run. We dismiss the one-time intro so it can't
  // preempt the demo screen, and clear any leftover demo state for a clean start.
  const handleTryDemo = () => {
    clearActiveRun(DEMO_KEY);
    setIntroSeenLocal();
    if (user) setIntroSeenRemote(user.id).catch(() => {});
    setIntroDecision("hide");
    setDemoActive(true);
  };

  // Leave the demo ("Play for Real" / Exit) → back to the welcome screen, where
  // the real run can be begun, continued, or viewed.
  const handleExitDemo = () => {
    clearActiveRun(DEMO_KEY);
    setDemoActive(false);
    setModeChosen(false);
  };

  let nextLabel = "Next Wheel";
  if (phase === "alignment" && pendingOption?.name === "Sidekick") {
    nextLabel = "Spin Again";
  } else if (phase === "fight") {
    nextLabel = fightStep === "outcome" ? "Back to Results" : "Reveal Outcome";
  } else if (isLastStep) {
    nextLabel = "See Results";
  }

  const handleSpin = () => {
    unlockAudio();
    spin();
  };

  // Auto-dismiss the "run restored" banner after a few seconds.
  useEffect(() => {
    if (!showRestoredToast) return;
    const t = setTimeout(dismissRestoredToast, 5000);
    return () => clearTimeout(t);
  }, [showRestoredToast, dismissRestoredToast]);

  const prevChaosAnnounce = useRef(false);
  useEffect(() => {
    const announcing = chaosOverlayStage === "type";
    if (announcing && !prevChaosAnnounce.current) {
      playChaosSound();
    }
    prevChaosAnnounce.current = announcing;
  }, [chaosOverlayStage]);

  // Broadcast our live progress to the opponent whenever it meaningfully
  // changes. lastResult is intentionally read fresh (not in deps) — it always
  // moves together with the score, which is in deps.
  useEffect(() => {
    if (!inMatch) return;
    const last = results[results.length - 1];
    publish({
      name: selfName,
      role: matchRole,
      score: totalScore,
      rank,
      alignmentLabel,
      wheelNumber,
      totalWheels,
      phase,
      finished: phase === "complete",
      lastResult: last
        ? {
            wheelName: last.wheelName,
            name: last.option.name,
            value: last.option.value,
          }
        : null,
      updatedAt: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inMatch,
    opponentPresent,
    totalScore,
    phase,
    wheelNumber,
    totalWheels,
    alignmentLabel,
    rank,
    selfName,
  ]);

  // Broadcast each main-wheel spin the instant it starts, so a spectating
  // opponent can replay the same animation. The result is already decided at
  // spin-start (pendingOption), so we send the wheel's options + the landing
  // index. Chaos/crown overlay spins use separate tokens and aren't mirrored —
  // the spectator still sees their score move via the panel.
  useEffect(() => {
    if (!inMatch || spinToken <= 0 || !pendingOption || !currentWheel) return;
    const resultIndex = currentWheel.options.indexOf(pendingOption);
    if (resultIndex < 0) return;
    spinSeqRef.current += 1;
    publishSpin({
      seq: spinSeqRef.current,
      wheelName: currentWheel.name,
      options: currentWheel.options.map((o) => ({
        name: o.name,
        value: o.value,
      })),
      resultIndex,
      showValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinToken]);

  // 2s cooldown after a result lands before the player can advance.
  const [cooldownElapsed, setCooldownElapsed] = useState(false);
  const [countdown, setCountdown] = useState(2);
  useEffect(() => {
    if (!showResult) {
      setCooldownElapsed(false);
      setCountdown(2);
      return;
    }
    setCooldownElapsed(false);
    setCountdown(2);
    const start = Date.now();
    const interval = setInterval(() => {
      const remaining = Math.ceil((2000 - (Date.now() - start)) / 1000);
      if (remaining <= 0) {
        setCountdown(0);
        setCooldownElapsed(true);
        clearInterval(interval);
      } else {
        setCountdown(remaining);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [showResult]);

  // The button stays gray while a chaos event is pending/active; with no
  // chaos it unlocks right after the 2s cooldown.
  const chaosBusy = chaosPending || chaosOverlayStage !== null;
  const advanceReady = cooldownElapsed && !chaosBusy;

  const spinButton = {
    label: spinning ? "..." : "Spin",
    onClick: handleSpin,
    disabled: spinning || showResult,
  };

  // Advance button is only mounted once the wheel has stopped on a result, so
  // it can never appear mid-spin. It slides down into view on mount.
  const advanceFooter = showResult ? (
    <button
      onClick={next}
      disabled={!advanceReady}
      className="animate-slide-down min-w-[10rem] rounded-full bg-accent px-[clamp(1.75rem,3.5vw,2.5rem)] py-[clamp(0.5rem,1.5vh,0.75rem)] text-[clamp(0.9rem,1.8vw,1.125rem)] font-bold uppercase tracking-widest text-white shadow-[0_0_30px_rgba(124,58,237,0.5)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
    >
      {advanceReady ? nextLabel : cooldownElapsed ? "..." : `${countdown}`}
    </button>
  ) : null;

  if (introDecision === "pending") {
    return <main className="min-h-screen" />;
  }

  if (introDecision === "show" && !demoActive) {
    return (
      <IntroScreen
        runStatus={runStatus}
        isGuest={!user}
        onContinue={handleIntroContinue}
        onTryDemo={handleTryDemo}
      />
    );
  }

  // Pick the screen to show. Everything from here on is rendered as a sibling
  // of a single persistent <MusicPlayer/> (see the return below), so the music
  // keeps playing uninterrupted as the player moves between the menu, the
  // lobby, and the game — and never stacks a second track.
  let screen: React.ReactNode;

  // Demo takes precedence over every other screen while active, and is rendered
  // as a sibling of the persistent <MusicPlayer/> below so the music keeps
  // playing across menu ↔ demo ↔ game.
  if (demoActive) {
    screen = <DemoRun onExit={handleExitDemo} />;
  } else if (!hydrated && !inMatch) {
    // The solo run now loads only after auth settles (so we read the right
    // per-account slot rather than the guest one). Show a neutral placeholder
    // in that brief window instead of flashing the fresh alignment wheel.
    screen = <main className="min-h-[100dvh]" />;
  } else if (hydrated && !inMatch && !modeChosen) {
    // Landing screen: shown on every load until the player picks a mode (and
    // when not joining via a match link). A mid-run offers "Continue Run"; a
    // finished run offers "View Stats" (and unlocks Play vs Friend) — neither
    // path resets the run, so navigating back here can never wipe progress.
    screen = (
      <ModeSelect
        isGuest={!user}
        runStatus={runStatus}
        selfName={selfName}
        onContinue={() => setModeChosen(true)}
        onSolo={() => setModeChosen(true)}
        onPlayFriend={handlePlayFriend}
        onTryDemo={handleTryDemo}
      />
    );
  } else if (inMatch && matchNullified) {
    // Opponent abandoned the match — return to the menu (the solo run, kept on
    // its own slot, is waiting there untouched).
    screen = (
      <MatchEnded
        opponentName={opponentNameRef.current}
        onExit={exitMatchToMenu}
      />
    );
  } else if (
    inMatch &&
    matchRole === "host" &&
    !opponentPresent &&
    !opponentEverPresentRef.current
  ) {
    // Host is waiting for a friend to open the shared link. Only before the
    // first join — if the opponent joins then drops, we keep showing the game
    // (panel marks them Offline) until the leave-watcher voids the match.
    screen = (
      <MatchLobby
        shareLink={buildShareLink(matchId!)}
        selfName={selfName}
        onCancel={exitMatchToMenu}
      />
    );
  } else {
    screen = (
    <main className="flex min-h-[100dvh] flex-col items-center">
      {showRestoredToast && (
        <div className="animate-slide-down fixed left-1/2 top-4 z-50 -translate-x-1/2 rounded-full border border-accent/40 bg-background/90 px-5 py-2 text-xs font-bold uppercase tracking-widest text-accent shadow-[0_0_30px_rgba(124,58,237,0.4)] backdrop-blur">
          Run restored — continue where you left off
        </div>
      )}
      {/* Running list of the buffs/nerfs picked up from the chaos wheel,
          pinned to the left of the wheel for the whole run. */}
      {phase !== "complete" && <ChaosEffectsPanel effects={chaosEffects} />}
      {/* Spectate view: once you've finished and your friend is still going,
          watch their wheel spin live (desktop — hidden on small screens to
          keep the results readable). */}
      {inMatch &&
        phase === "complete" &&
        opponent &&
        !opponent.finished &&
        opponentPresent && (
          <div className="fixed left-4 top-1/2 z-30 hidden w-[min(40vw,18rem)] -translate-y-1/2 flex-col items-center gap-2 rounded-2xl border border-white/15 bg-background/85 p-4 shadow-[0_0_30px_rgba(0,0,0,0.5)] backdrop-blur md:flex">
            <div className="text-[0.65rem] font-bold uppercase tracking-widest text-accent">
              Spectating {opponent.name}
            </div>
            <SpectatorWheel spin={opponentSpin} />
            <div className="text-center text-xs text-white/50">
              {opponent.wheelNumber > 0
                ? `Wheel ${opponent.wheelNumber} of ${opponent.totalWheels}`
                : "Spinning…"}
            </div>
          </div>
        )}
      <div className="fixed right-2 top-2 z-40 flex gap-1.5 md:right-4 md:top-4 md:gap-2">
        <TokenBalance
          onClick={() => setPanel((p) => (p === "tokens" ? null : "tokens"))}
        />
        <button
          onClick={() =>
            setPanel((p) => (p === "leaderboard" ? null : "leaderboard"))
          }
          aria-label="Leaderboard"
          className={`rounded-full border bg-background/80 px-2.5 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest backdrop-blur transition md:px-4 md:py-2 md:text-xs ${
            panel === "leaderboard"
              ? "border-accent text-accent"
              : "border-white/20 text-white/70 hover:border-accent hover:text-accent"
          }`}
        >
          <span className="md:hidden">🏆</span>
          <span className="hidden md:inline">Leaderboard</span>
        </button>
        <Link
          href={user ? "/profile" : "/login"}
          className="max-w-[5.5rem] truncate rounded-full border border-white/20 bg-background/80 px-2.5 py-1.5 text-[0.65rem] font-bold uppercase tracking-widest text-white/70 backdrop-blur transition hover:border-accent hover:text-accent md:max-w-none md:px-4 md:py-2 md:text-xs"
        >
          {user ? profile?.username ?? "Profile" : "Sign In"}
        </Link>
      </div>
      <LeaderboardPanel
        open={panel === "leaderboard"}
        onClose={() => setPanel(null)}
      />
      <TokensPanel open={panel === "tokens"} onClose={() => setPanel(null)} />
      <ScoreDisplay
        score={totalScore}
        rank={rank}
        alignmentLabel={alignmentLabel}
        hasCrown={hasCrown}
      />

      {/* Match scoreboard — a right-side card on desktop, an in-flow strip
          above the wheel on mobile (so it can't cover the wheel). */}
      {inMatch && (
        <OpponentPanel
          selfName={selfName}
          opponent={opponent}
          opponentPresent={opponentPresent}
          selfFinished={phase === "complete"}
          selfScore={totalScore}
          onLeave={exitMatchToMenu}
        />
      )}

      <div className="flex w-full flex-1 flex-col items-center px-4 pb-24 pt-3 md:pb-28 md:pt-[clamp(1.25rem,4vh,2.5rem)]">
        {phase !== "complete" && currentWheel && (
          <>
            <div className="mb-3 text-center md:mb-[clamp(1rem,2.5vh,1.5rem)]">
              <div className="text-[clamp(0.7rem,1.3vw,0.875rem)] uppercase tracking-widest text-white/40">
                {phase === "alignment"
                  ? "Determine Your Alignment"
                  : phase === "fight"
                    ? "Bonus: Pick a Fight"
                    : `Wheel ${wheelNumber} of ${totalWheels}`}
              </div>
              <h1 className="mt-1 text-[clamp(1.5rem,3.5vw,2.25rem)] font-extrabold">
                {currentWheel.name}
              </h1>
            </div>

            <SpinningWheel
              wheel={currentWheel}
              spinToken={spinToken}
              resultOption={pendingOption}
              onSpinEnd={handleSpinEnd}
              onTick={playTick}
              spinButton={spinButton}
              footer={advanceFooter}
              showResult={showResult}
              showValue={showValue}
              heat={heatLevel}
            />

            {(phase === "main" || phase === "abilities") && (
              <ChaosControl
                cooldownLabel={chaosCooldownLabel}
                ready={canTriggerChaos}
                disabled={spinning || showResult || chaosBusy}
                onTrigger={triggerChaosManually}
              />
            )}
          </>
        )}

        {phase === "complete" && (
          <ResultsSummary
            results={results}
            chaosResults={chaosResults}
            fightResults={fightResults}
            onPickFight={startFight}
            onReroll={reset}
            onGetTokens={() => setPanel("tokens")}
            onSubmitted={markSubmitted}
            submittedScoreId={submittedScoreId}
            totalScore={totalScore}
            rank={rank}
            alignmentLabel={alignmentLabel}
            leaderboardBoard={leaderboardBoard}
            isSidekick={isSidekick}
            hasCrown={hasCrown}
            cooldownStartedAt={cooldownStartedAt}
            isMultiplayer={inMatch}
            onBackToMenu={inMatch ? exitMatchToMenu : () => setModeChosen(false)}
            onRerollWheel={rerollWheel}
            wheelRerollsUsed={wheelRerollsUsed}
          />
        )}
      </div>

      <ChaosWheelOverlay
        stage={chaosOverlayStage}
        outcome={chaosOutcome}
        typeSpinToken={chaosTypeSpinToken}
        resultSpinToken={chaosResultSpinToken}
        onTypeSpinEnd={handleChaosTypeSpinEnd}
        onResultSpinEnd={handleChaosResultSpinEnd}
        onDismiss={dismissChaosOverlay}
        onTick={playTick}
      />

      <CrownOverlay
        stage={crownStage}
        spinToken={crownSpinToken}
        pendingOption={crownPendingOption}
        bonusOption={crownBonusOption}
        onTakeCrown={takeCrown}
        onSpin={spinCrownWheel}
        onSpinEnd={handleCrownSpinEnd}
        onContinue={continueAfterCrown}
        onTick={playTick}
      />
    </main>
    );
  }

  return (
    <>
      {screen}
      {/* One persistent music player across every screen — so background
          music plays on the menu, never restarts when entering the game, and
          can't stack overlapping tracks. */}
      <MusicPlayer />
    </>
  );
}
